import './Templates.scss'
import { useNavigate, useSearchParams } from 'react-router-dom'
import React from 'react'
import { type SchedulingEmailsTab, TemplateLookup } from '../../../models/TemplateLibrary'
import { SCHEDULING_EMAIL_TAB_KEYS, SCHEDULING_EMAIL_TEMPLATE_TYPE, TEMPLATE_TABS, TEMPLATE_TYPES } from './TemplatesTab'
import { ClickNavigation } from '../../../components/NavigationWithSideBar/ClickNavigation'
import { TemplatesTabView } from './TemplatesTabView'
import { Button } from '../../../components/Button/Button'
import { useSchedulingTemplatesEnabled } from '../../Scheduling/shared/useSchedulingTemplatesEnabled'
import { SchedulingEmailsTabView } from './SchedulingEmails/SchedulingEmailsTabView'
import { useSchedulingEmails } from './SchedulingEmails/useSchedulingEmails'
import { type GenerateTemplatePageState } from './GenerateTemplate'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { useAccount } from '../../../common/useAccount'

export const Templates = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isEnabled: schedulingTemplatesEnabled } = useSchedulingTemplatesEnabled()
  const { hasScope } = useZocksAuth()
  const { account } = useAccount()
  const hasTemplateAdminScope = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin'])
  const canAddSchedulingTemplate = hasTemplateAdminScope && !(account?.enterpriseConfig.lockTemplates ?? true)
  const visibleTabs = schedulingTemplatesEnabled ? TEMPLATE_TABS : TEMPLATE_TABS.filter((tab) => !SCHEDULING_EMAIL_TAB_KEYS.includes(tab.key))
  const requestedCategoryId = searchParams.get('category')
  const activeCategoryId =
    requestedCategoryId != null && visibleTabs.some((tab) => tab.key === requestedCategoryId) ? requestedCategoryId : TEMPLATE_TABS[0].key

  const onClick: (templateLookup: TemplateLookup) => void = (templateLookup) => {
    navigate(`/workspace/setup/templates/template/${templateLookup.id}`)
  }

  const handleSectionClick = (id: string) => {
    setSearchParams({ category: id }, { replace: true })
  }

  const sections = visibleTabs.map((tab) => {
    const isGroup = !('searchParams' in tab) && !('audience' in tab)
    return {
      id: tab.key,
      label: <span className={isGroup ? 'template-group' : 'template-tab'}>{tab.label}</span>,
      title: 'header' in tab ? tab.header : tab.label,
      disabled: isGroup,
      content: () =>
        'searchParams' in tab ? (
          <TemplatesTabView templatesTab={tab} onClick={onClick} />
        ) : 'audience' in tab ? (
          <SchedulingEmailsTabView tab={tab} onClick={onClick} />
        ) : null,
    }
  })

  const activeTab = TEMPLATE_TYPES.find((tab) => tab.key === activeCategoryId)
  const activeSchedulingTab = visibleTabs.find((tab): tab is SchedulingEmailsTab => 'audience' in tab && tab.key === activeCategoryId)
  const isYourTemplatesTab = activeCategoryId === 'your-templates'
  const meetingSummaryTab = TEMPLATE_TYPES.find((tab) => tab.key === 'meeting-summary')!
  const addTemplateTab = isYourTemplatesTab ? meetingSummaryTab : activeTab
  const { emails: schedulingEmails } = useSchedulingEmails({ skip: activeSchedulingTab == null || !canAddSchedulingTemplate })
  const firstSchedulingEmail = activeSchedulingTab ? schedulingEmails.find((email) => email.audience === activeSchedulingTab.audience) : undefined

  const addTemplateState: GenerateTemplatePageState | undefined = firstSchedulingEmail
    ? { templateTypeName: firstSchedulingEmail.label, templateType: SCHEDULING_EMAIL_TEMPLATE_TYPE, templateTypeId: firstSchedulingEmail.slug }
    : addTemplateTab
      ? {
          templateTypeName: addTemplateTab.label,
          templateType: addTemplateTab.searchParams.type ?? '',
          templateTypeId: addTemplateTab.searchParams.typeId ?? addTemplateTab.key,
        }
      : undefined

  const showsAddTemplate = activeSchedulingTab ? canAddSchedulingTemplate : addTemplateState != null

  const addTemplateButton = showsAddTemplate ? (
    <Button
      variant='primary'
      className='d-flex gap-2 zocks-mb-0.75 w-100 px-0'
      disabled={addTemplateState == null}
      onClick={() => navigate('/workspace/setup/templates/template/create', { state: addTemplateState })}
    >
      <i className='bi bi-plus' />
      Add template
    </Button>
  ) : null

  return (
    <div className='d-flex gap-5 templates-tab-wrapper'>
      <ClickNavigation
        className='w-100'
        sections={sections}
        defaultActiveSectionId={activeCategoryId}
        activeSectionId={activeCategoryId}
        onSectionClick={handleSectionClick}
        NavigationHeader={addTemplateButton}
      />
    </div>
  )
}
